// Who can do what in the admin portal: a level (NONE, VIEW, MANAGE) in each
// area. The server sends each person's levels as `user.access` from
// /api/auth/me; these are the same values as backend/services/access.js
// (AREAS, LEVEL_RANK and the BUILT_IN grids). Keep the two in step.
// They're only used when `access` is missing (an older saved copy of /me).

export const AREAS = [
  'sessions', 'sessionSetup', 'registers', 'competitions', 'skillTracking', 'skillSetup',
  'firstAid', 'welfare', 'staffDocuments', 'members', 'payments', 'communications',
  'shop', 'business', 'settings', 'auditLog',
];

// What each area is called, and what's in it, on the Roles pages
export const AREA_LABELS = {
  sessions: 'Sessions',
  sessionSetup: 'Session setup',
  registers: 'Registers',
  competitions: 'Competitions',
  skillTracking: 'Skill tracking',
  skillSetup: 'Skill setup',
  firstAid: 'First aid',
  welfare: 'Welfare',
  staffDocuments: 'Staff documents',
  members: 'Members',
  payments: 'Payments',
  communications: 'Communications',
  shop: 'Shop',
  business: 'Business',
  settings: 'Club settings',
  auditLog: 'Audit log',
};

// What's in each area, part by part, for the hint under it on the Roles
// pages. A part with a `feature` (backend/config/features.js) is only
// mentioned when the club has that feature on; a `sentence` part starts a new
// sentence instead of carrying on the list.
const ifOn = (text, feature) => ({ text, feature });
export const AREA_HINT_PARTS = {
  sessions: ['Sessions calendar', "a session's page", ifOn('coaches on a day', 'sessionCoaches')],
  sessionSetup: ['Session management', 'Session types', 'Closures', ifOn('Coach hours', 'sessionCoaches')],
  registers: ["Registers and check-in, including gymnasts' medical needs, emergency contacts and dates of birth"],
  competitions: [
    ifOn('Competitions', 'competitions'), ifOn('Requirements', 'requirements'),
    ifOn("gymnasts' dates of birth, and the medical needs and emergency contacts of gymnasts entered", 'competitions'),
  ],
  skillTracking: [
    "Gymnasts' progress", ifOn('Routines to check', 'gymnastRoutines'), ifOn('Training diaries', 'diary'), 'coach notes',
    { text: 'Medical needs, emergency contacts and dates of birth need Members or First aid', sentence: true },
  ],
  skillSetup: ['Levels', 'All skills', ifOn('Certificates', 'certificates'), ifOn('Certificate setup', 'certificates')],
  firstAid: ['First aid reports, and the gymnast list with medical needs, emergency contacts and dates of birth'],
  welfare: ['Welfare reports'],
  staffDocuments: ["Everyone's staff documents and signatures"],
  members: ['Members and their profiles', 'gymnast details (including medical needs and emergency contacts)', ifOn('BG numbers', 'bgNumbers')],
  payments: [ifOn('Memberships', 'memberships'), 'Credits', 'Charges', 'Payments', 'Card payments', 'Member payments'],
  communications: ['Post a notice', 'Messages', 'Email members', 'Recipient groups'],
  shop: ['Shop products and Shop orders'],
  business: ['Invoices and Business contacts'],
  settings: ['Club settings', 'Club branding', 'Help page'],
  auditLog: ['Audit log'],
};

// Areas that only exist with a feature on: shown when any of these is on
const AREA_FEATURES = {
  competitions: ['competitions', 'requirements'],
  staffDocuments: ['codeOfConduct'],
  shop: ['shop'],
  business: ['invoices'],
};

const allOn = () => true;

// Whether an area means anything to this club. `featureOn(key)` is whether
// the club has a feature on (hooks/useFeature.js useFeatures).
export const areaShown = (area, featureOn = allOn) =>
  !AREA_FEATURES[area] || AREA_FEATURES[area].some(featureOn);

// The hint for an area, mentioning only what this club has switched on
export function areaHint(area, featureOn = allOn) {
  const parts = (AREA_HINT_PARTS[area] || [])
    .map(p => (typeof p === 'string' ? { text: p } : p))
    .filter(p => !p.feature || featureOn(p.feature));
  return parts.reduce((out, p) => {
    if (!out) return p.text;
    return `${out}${p.sentence ? '. ' : ', '}${p.text}`;
  }, '');
}

export const LEVEL_RANK = { NONE: 0, VIEW: 1, MANAGE: 2 };

export const STAFF_ROLES = ['CLUB_ADMIN', 'COACH', 'WELFARE', 'STAFF'];

const grid = (levels) => Object.fromEntries(AREAS.map(a => [a, levels[a] || 'NONE']));
const M = 'MANAGE';
const V = 'VIEW';
const fullAccess = () => grid(Object.fromEntries(AREAS.map(a => [a, M])));

// The starting access of each built-in role
export const BUILT_IN_ACCESS = {
  CLUB_ADMIN: fullAccess(),
  COACH: grid({ sessions: M, registers: M, competitions: M, skillTracking: M, firstAid: M, members: M }),
  WELFARE: grid({ sessions: M, registers: M, competitions: M, skillTracking: M, firstAid: M, welfare: M, staffDocuments: V, members: M }),
  FINANCE: grid({ payments: M, shop: V, business: M }),
};

// The built-in access for someone's role, for when the server hasn't said.
// A super admin who has opened a club gets everything but Welfare.
export function builtInAccessFor(role, actingAs) {
  if (!STAFF_ROLES.includes(role)) return grid({});
  if (actingAs) return { ...fullAccess(), welfare: 'NONE' };
  return BUILT_IN_ACCESS[role] || grid({});
}

// can(area, level) for a set of levels
export const canFor = (access) => (area, level = 'VIEW') =>
  LEVEL_RANK[access?.[area] || 'NONE'] >= LEVEL_RANK[level];
